import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import UserFormModal from '../../components/admin/UserFormModal';
import { ADMIN_API_URL } from '../../constants';
import { useSelector } from 'react-redux';
import showToast from '../../utils/toast';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const UserManagement = ({ addNotification }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [availableManagers, setAvailableManagers] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [rolesError, setRolesError] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState(null);
  const tokenExpirationHandledRef = useRef(false);
  const { user: loggedInUser } = useSelector(state => state.auth);
  const isSuperAdmin = loggedInUser?.role === 'super_admin';

  // Helper function to check for token expiration
  const handleTokenExpiration = (error) => {
    // If already handled, skip to avoid duplicate notifications (synchronous check with useRef)
    if (tokenExpirationHandledRef.current) {
      return true;
    }

    if (error && error.name === "TokenExpiredError" || 
        (error.message && error.message.includes("jwt expired")) ||
        (typeof error === 'object' && error.message && error.message.name === "TokenExpiredError")) {
      // Mark as handled immediately (synchronous) to prevent duplicate calls
      tokenExpirationHandledRef.current = true;
      showToast("Session expired. Please login again.", 'warning');
      addNotification("Session expired. Please login again.", 'warning');
      // Clear session storage
      sessionStorage.removeItem("authToken");
      sessionStorage.removeItem("userInfo");
      // Redirect to login
      setTimeout(() => {
        navigate("/");
      }, 2000);
      return true;
    }
    return false;
  };

  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    if (!token) {
      setUsersError("Authorization token not found. Please log in again.");
      window.location.href = "/";
      return;
    }
    fetchUsers();
    // Both super admin and sub-users can fetch roles (sub-users need to create users with lower roles)
    fetchAvailableRoles();
    fetchAvailablePermissions();
    // Fetch available managers for Super Admin only
    if (isSuperAdmin) {
      fetchAvailableManagers();
    }
  }, [isSuperAdmin]);

  const fetchUsers = async () => {
    setLoadingUsers(true);
    setUsersError(null);
    const token = sessionStorage.getItem("authToken");
    if (!token) {
      setUsersError("Authorization token not found.");
      setLoadingUsers(false);
      return;
    }
    try {
      const response = await fetch(`${ADMIN_API_URL}users`, {
        method: "GET",
        headers: {
          "Authorization": `${token}`
        }
      });
      const result = await response.json();
      
      // Check for token expiration
      if (result.message && (result.message.name === "TokenExpiredError" || result.message.message === "jwt expired")) {
        if (handleTokenExpiration(result.message)) {
          setLoadingUsers(false);
          return;
        }
      }
      
      if (!response.ok) {
        throw new Error(result.message || "Failed to fetch users.");
      }
      setUsers(result.data);
    } catch (err) {
      // Check for token expiration in error
      if (handleTokenExpiration(err)) {
        setLoadingUsers(false);
        return;
      }
      setUsersError(err.message);
      addNotification(`Error fetching users: ${err.message}`, 'error');
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchAvailableRoles = async () => {
    setLoadingRoles(true);
    setRolesError(null);
    const token = sessionStorage.getItem("authToken");
    if (!token) {
      setRolesError("Authorization token not found.");
      setLoadingRoles(false);
      return;
    }
    try {
      // Use forUserCreation=true to get only roles below current user's level in hierarchy
      const response = await fetch(`${ADMIN_API_URL}roles?forUserCreation=true`, {
        method: "GET",
        headers: {
          "Authorization": `${token}`
        }
      });
      const result = await response.json();
      
      // Check for token expiration
      if (result.message && (result.message.name === "TokenExpiredError" || result.message.message === "jwt expired")) {
        if (handleTokenExpiration(result.message)) {
          setLoadingRoles(false);
          return;
        }
      }
      
      if (!response.ok) {
        const errorData = result;
        throw new Error(errorData.message || "Failed to fetch roles.");
      }
      // Backend already returns only available roles based on hierarchy
      // Also filter out current user's role as additional safety
      const allRoles = result.data.map(role => role.roleName);
      const currentUserRole = loggedInUser?.role;
      const filteredRoles = allRoles.filter(role => {
        // Trim whitespace for comparison (API might return roles with trailing spaces)
        const roleTrimmed = (role || '').trim();
        const userRoleTrimmed = (currentUserRole || '').trim();
        return roleTrimmed.toLowerCase() !== userRoleTrimmed.toLowerCase();
      });
      setAvailableRoles(filteredRoles); 
    } catch (err) {
      // Check for token expiration in error
      if (handleTokenExpiration(err)) {
        setLoadingRoles(false);
        return;
      }
      setRolesError(err.message);
      // Don't show error notification for 403 (permission denied) - it's expected for non-super-admin
      // Only show for other errors
      if (!err.message.includes('Forbidden') && !err.message.includes('403')) {
        addNotification(`Error fetching roles: ${err.message}`, 'error');
      }
      // Set empty array so UI can handle it gracefully
      setAvailableRoles([]);
    } finally {
      setLoadingRoles(false);
    }
  };

  const fetchAvailablePermissions = async () => {
    const token = sessionStorage.getItem("authToken");
    if (!token) return;
    try {
      const response = await fetch(`${ADMIN_API_URL}permissions`, {
        method: "GET",
        headers: {
          "Authorization": `${token}`
        }
      });
      const result = await response.json();
      
      // Check for token expiration
      if (result.message && (result.message.name === "TokenExpiredError" || result.message.message === "jwt expired")) {
        if (handleTokenExpiration(result.message)) {
          return;
        }
      }
      
      if (!response.ok) throw new Error(result.message || "Failed to fetch permissions.");
      setAvailablePermissions(result.data.map(p => p.permissionName));
    } catch (err) {
      // Check for token expiration in error
      if (handleTokenExpiration(err)) {
        return;
      }
      addNotification(`Error fetching permissions: ${err.message}`, 'error');
    }
  };

  const fetchAvailableManagers = async () => {
    const token = sessionStorage.getItem("authToken");
    if (!token) return;
    try {
      const response = await fetch(`${ADMIN_API_URL}users`, {
        method: "GET",
        headers: {
          "Authorization": `${token}`
        }
      });
      const result = await response.json();
      
      // Check for token expiration
      if (result.message && (result.message.name === "TokenExpiredError" || result.message.message === "jwt expired")) {
        if (handleTokenExpiration(result.message)) {
          return;
        }
      }
      
      if (!response.ok) throw new Error(result.message || "Failed to fetch managers.");
      // Filter: Only active users, exclude current user, exclude Interns (if they can't be managers)
      const managers = result.data.filter(user => 
        user.id !== loggedInUser?.id &&
        user.status === 'Active' &&
        user.role !== 'Intern' // Exclude Interns as they typically can't be managers
      );
      setAvailableManagers(managers);
    } catch (err) {
      // Check for token expiration in error
      if (handleTokenExpiration(err)) {
        return;
      }
      console.error("Error fetching managers:", err);
      // Don't show error notification as this is optional
    }
  };

  // Helper to resolve reportingTo (manager) display name
  const getReportingToName = (userObj) => {
    const reportingTo = userObj?.reportingTo;
    // If this user is super_admin, show its own name/role
    if ((userObj?.role || '').toLowerCase() === 'super_admin') {
      return userObj.name || userObj.username || 'super_admin';
    }

    // If reportingTo is falsy, assume top-level and show Super Admin if present
    if (!reportingTo) {
      const superAdminUser = users.find(u => (u.role || '').toLowerCase() === 'super_admin') || availableManagers.find(m => (m.role || '').toLowerCase() === 'super_admin');
      if (superAdminUser) return superAdminUser.name || superAdminUser.username || 'super_admin';
      return 'N/A';
    }

    // If reportingTo is an id (string), try to find in users or availableManagers
    if (typeof reportingTo === 'string') {
      const managerFromUsers = users.find(u => u.id === reportingTo);
      if (managerFromUsers) return managerFromUsers.name || managerFromUsers.username || 'Unknown';
      const managerFromAvailable = availableManagers.find(m => m.id === reportingTo);
      if (managerFromAvailable) return managerFromAvailable.name || managerFromAvailable.username || 'Unknown';
      return reportingTo; // fallback show id
    }
    // If reportingTo is object, use its name
    if (typeof reportingTo === 'object') return reportingTo.name || reportingTo.username || 'N/A';
    return 'N/A';
  };

  const handleCreate = () => {
    setEditUser(null);
    setShowModal(true);
  };

  const handleEdit = (user) => {
    setEditUser(user);
    setShowModal(true);
  };

  const handleDelete = (userId) => {
    setUserToDelete(userId);
    setDeleteErrorMessage(null);
    setShowDeleteConfirmModal(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) {
      setDeleteErrorMessage("No user selected for deletion.");
      setShowDeleteConfirmModal(true);
      return;
    }

    const token = sessionStorage.getItem("authToken");
    if (!token) {
      setDeleteErrorMessage("Authorization token not found. Please log in again.");
      setShowDeleteConfirmModal(true);
      return;
    }

    try {
      const response = await fetch(`${ADMIN_API_URL}users/${userToDelete}`, {
        method: "DELETE",
        headers: {
          "Authorization": `${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete user.");
      }

      addNotification("User deleted successfully.", 'success');
      setShowDeleteConfirmModal(false);
      setUserToDelete(null);
      setUsers(prevUsers => prevUsers.filter(u => u.id !== userToDelete)); // Update UI
    } catch (err) {
      setDeleteErrorMessage(`Error: ${err.message}`);
      setShowDeleteConfirmModal(true);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditUser(null);
  };

  const handleModalSave = async (user) => {
    const token = sessionStorage.getItem("authToken");
    if (!token) {
      addNotification("Authorization token not found. Please log in again.", 'error');
      return;
    }

    setLoadingUsers(true); // Set loading for user operations
    setUsersError(null); // Clear previous errors

    try {
      let response;
      let method;
      let url;
      
      const userDataToSend = { ...user, roleName: user.role };
      // Remove the original 'role' field if it exists to avoid sending both
      delete userDataToSend.role;
      // Remove email if empty string
      if (userDataToSend.email === '') delete userDataToSend.email;
      // Convert empty reportingTo string to null for backend
      if (userDataToSend.reportingTo === '') {
        userDataToSend.reportingTo = null;
      }

      if (user.id) {
        // Update existing user
        method = "PATCH";
        url = `${ADMIN_API_URL}users/${user.id}`;
      } else {
        // Create new user
        method = "POST";
        url = `${ADMIN_API_URL}users`;
      }

      response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          "Authorization": `${token}`,
        },
        body: JSON.stringify(userDataToSend),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to ${user.id ? 'update' : 'create'} user.`);
      }

      const result = await response.json();
      addNotification(`User ${user.id ? 'updated' : 'created'} successfully.`, 'success');
      setShowModal(false);
      setEditUser(null);
      fetchUsers(); // Re-fetch users to update the list
    } catch (err) {
      addNotification(`Error ${user.id ? 'updating' : 'creating'} user: ${err.message}`, 'error');
      console.error(`Error ${user.id ? 'updating' : 'creating'} user:`, err);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Filter permissions: super admin gets all, others get only their assignablePermissions
  const assignablePermissions = isSuperAdmin
    ? availablePermissions
    : availablePermissions.filter(p => loggedInUser?.assignablePermissions?.includes(p));

  // Filter out current user's role from available roles for modal
  // Both super admin and sub-users should not be able to create users with their own role
  const rolesForModal = availableRoles.filter(role => role !== loggedInUser?.role);

  return (
    <>
      <Header />
      <div id="wrapper" style={{ paddingTop: '70px' }}>
        <div className="content">
          <div className="row">
            <div className="col-lg-12">
              <div className="hpanel">
                <div className="panel-body">
                  <h2 className="font-light m-b-xs">User Management</h2>
                  <button className="btn btn-primary m-b-md" onClick={handleCreate}>Create User</button>
                  {loadingRoles && <p>Loading roles...</p>}
                  {rolesError && <p className="text-danger">Error loading roles: {rolesError}</p>}
                  {loadingUsers && <p>Loading users...</p>}
                  {usersError && <p className="text-danger">Error loading users: {usersError}</p>}
                  <div className="table-responsive">
                    <table className="table table-striped table-hover">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Username</th>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Assign To</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.length > 0 ? (
                          users.map(user => (
                            <tr key={user.id}>
                              <td>{user.name}</td>
                              <td>{user.username}</td>
                              <td>{user.email || ''}</td>
                              <td>{user.role}</td>
                              <td>{getReportingToName(user)}</td>
                              <td>{user.status}</td>
                              <td>
                                <div className="dropdown">
                                  <button
                                    className="btn btn-sm dropdown-toggle"
                                    type="button"
                                    id={`dropdownMenuButton-user-${user.id}`}
                                    data-toggle="dropdown"
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                    style={{ backgroundColor: '#34495e', color: '#ffffff' }}
                                  >
                                    Actions <span className="caret"></span>
                                  </button>
                                  <ul className="dropdown-menu" aria-labelledby={`dropdownMenuButton-user-${user.id}`}>
                                    <li>
                                      <a
                                        className="dropdown-item"
                                        href="#"
                                        onClick={() => handleEdit(user)}
                                      >
                                        Edit
                                      </a>
                                    </li>
                                    <li>
                                      <a
                                        className="dropdown-item text-danger"
                                        href="#"
                                        onClick={() => handleDelete(user.id)}
                                      >
                                        Delete
                                      </a>
                                    </li>
                                  </ul>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" className="text-center">No users found.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
      {showModal && (
        <UserFormModal
          show={showModal}
          onClose={handleModalClose}
          onSave={handleModalSave}
          user={editUser}
          availableRoles={rolesForModal}
          availablePermissions={assignablePermissions}
          availableManagers={availableManagers}
          isSuperAdmin={isSuperAdmin}
          loggedInUser={loggedInUser}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && (
        <div className="modal" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog" style={{ maxWidth: '500px', marginTop: '10%' }}>
            <div className="modal-content" style={{ borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
              <div className="modal-header" style={{ padding: '20px 25px', borderBottom: '1px solid #e5e5e5' }}>
                <h4 className="modal-title" style={{ fontSize: '20px', fontWeight: '600', margin: 0 }}>
                  {deleteErrorMessage ? "Error" : "Confirm Deletion"}
                </h4>
                <button 
                  type="button" 
                  className="close" 
                  onClick={() => {
                    setShowDeleteConfirmModal(false);
                    setDeleteErrorMessage(null);
                    setUserToDelete(null);
                  }}
                  style={{ fontSize: '28px', opacity: 0.6, padding: '0', margin: '0' }}
                >
                  &times;
                </button>
              </div>
              <div className="modal-body" style={{ padding: '30px 25px', fontSize: '16px', lineHeight: '1.6' }}>
                {deleteErrorMessage ? (
                  <p className="text-danger" style={{ margin: 0, fontSize: '15px' }}>{deleteErrorMessage}</p>
                ) : (
                  <p style={{ margin: 0, color: '#333' }}>Are you sure you want to delete this user?</p>
                )}
              </div>
              <div className="modal-footer" style={{ padding: '20px 25px', borderTop: '1px solid #e5e5e5' }}>
                {deleteErrorMessage ? (
                  <button 
                    type="button" 
                    className="btn btn-default" 
                    onClick={() => {
                      setShowDeleteConfirmModal(false);
                      setDeleteErrorMessage(null);
                      setUserToDelete(null);
                    }}
                    style={{ padding: '8px 20px', fontSize: '14px' }}
                  >
                    Close
                  </button>
                ) : (
                  <>
                    <button 
                      type="button" 
                      className="btn btn-default" 
                      onClick={() => {
                        setShowDeleteConfirmModal(false);
                        setDeleteErrorMessage(null);
                        setUserToDelete(null);
                      }}
                      style={{ padding: '8px 20px', fontSize: '14px', marginRight: '10px' }}
                    >
                      Cancel
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-primary" 
                      onClick={confirmDeleteUser}
                      style={{ padding: '8px 25px', fontSize: '14px', fontWeight: '500' }}
                    >
                      OK
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserManagement; 